import React, { Fragment, useState } from "react";
import CopyToClipboard from "components/CopyToClipboard";
import Analytics, {
  COPY_SHORT_LINK_SHORT_ID,
  COPY_SHORT_LINK_URL
} from "utils/analytics";
import { useFormikContext } from "formik";
import { getShortLinkCheckoutUrlVariations } from "utils/shortLink";
import get from "lodash/get";
import ExternalLink from "components/ExternalLink";
import Token from "components/Token";
import { useCtxReferences } from "utils/selectors";
import SectionBezel from "components/SectionBezel";
import { ORGANIZATION } from "utils/constants/models";
import Toggle from "react-toggle";
import { getToggleProps } from "utils/form";
import { useCanEnableTestMode } from "utils/hooks";
import TestModeSetupPrompt from "components/TestModeSetupPrompt";

const LinkField = ({ label, url }) => (
  <Fragment>
    <label className="field__label fw5 db">{label}</label>
    <div className="flex flex-column w-100 pb3">
      <div className="flex flex-row items-center">
        <CopyToClipboard
          tooltip="Copy link"
          toast="Copied link to clipboard"
          value={url}
          onClick={() => {
            Analytics.track(COPY_SHORT_LINK_URL, {
              url: url
            });
          }}
        />
        <div className="lh-copy word-break-all pa2 bg-content--secondary ba br2 hairline-1 w-100">
          <ExternalLink
            href={url}
            copy={url}
            customClasses={{
              container: "w-100",
              link:
                "link flex flex-row items-center justify-between link__decoration--hover color-primary pointer w-100 word-break-all",
              copy: "f6 lh-title pr2"
            }}
          />
        </div>
      </div>
    </div>
  </Fragment>
);

const LinkPreviewFields = () => {
  const { values } = useFormikContext();
  const { short_id } = values;

  const [testMode, setTestMode] = useState(false);
  const { resource, application, merchantAccount } = useCtxReferences();
  const canEnable = useCanEnableTestMode();
  const hasTestClientKey = Boolean(merchantAccount.test_client_key);
  const isOrg = resource === ORGANIZATION;
  const variations = getShortLinkCheckoutUrlVariations({
    alias: get(application, "alias"),
    subdomain: get(application, "subdomain"),
    shortId: short_id
  });
  const subdomainOriginalUrl = variations.subdomain.original;
  const subdomainTestUrl = variations.subdomain.test;

  const aliasOriginalUrl = variations.alias.original;
  const aliasTestUrl = variations.alias.test;

  const standardOriginalUrl = variations.standard.original;
  const standardTestUrl = variations.standard.test;

  // Placeholder for QR code field
  // const admin = useSelector((state) =>
  //   getAdminContext(state, {
  //     applicationUUID: application.uuid
  //   })
  // );
  // const { prices, products } = useProductsPrices();
  // const { coupons } = useCouponPromotionCodes();
  // const taxRates = useTaxRates();
  // const shippingRates = useShippingRates();

  // const codePreviewTitle = getShortLinkPreviewTitle({
  //   shortLink: values,
  //   prices,
  //   products,
  //   coupons,
  //   taxRates,
  //   shippingRates
  // });
  // const codePreviewProps = {
  //   header: codePreviewTitle,
  //   href: subdomainOriginalUrl,
  //   size: 40,
  //   img: get(admin, "profile.businessIcon")
  // };
  const actionNode = (
    <div className="flex flex-row justify-between items-center">
      <div className="flex flex-row justify-between items-center">
        <label className="f6 fw5 db pr2">Test mode</label>
        <Toggle
          {...getToggleProps({
            active: testMode,
            setFieldValue: (_, active) => setTestMode(active)
          })}
        />
      </div>
    </div>
  );

  const linkNodes = [];
  if (isOrg) {
    const brandedUrl = testMode ? subdomainTestUrl : subdomainOriginalUrl;
    linkNodes.push(
      <LinkField key="branded" label="Branded" url={brandedUrl} />
    );
  } else if (application.alias) {
    const brandedUrl = testMode ? aliasTestUrl : aliasOriginalUrl;
    linkNodes.push(
      <LinkField key="branded" label="Branded" url={brandedUrl} />
    );
  }
  const standardUrl = testMode ? standardTestUrl : standardOriginalUrl;
  linkNodes.push(
    <LinkField key="standard" label="Standard" url={standardUrl} />
  );

  let linkContent = linkNodes;
  if (testMode) {
    if (!hasTestClientKey) {
      const promptInput = {
        customClasses: {
          header: "f5 fw6",
          container:
            "w-100 flex flex-column justify-between items-start bg-white bb hairline-1 pb3"
        }
      };
      if (!canEnable) {
        promptInput.header = "Test mode disabled";
        promptInput.subheader =
          "Your account is not yet enabled for test mode. Please ensure your Stripe verification details have been submitted.";
        promptInput.actions = [];
      }

      linkContent = (
        <div className="pb3">
          <TestModeSetupPrompt {...promptInput} />
        </div>
      );
    }
  }

  return (
    <SectionBezel
      header={`Link${linkNodes.length > 1 ? "s" : ""}`}
      action={actionNode}
    >
      <div className="flex flex-column">
        {linkContent}
        <label className="field__label fw5 db">ID</label>
        <div className="flex flex-row items-center pb3">
          <CopyToClipboard
            tooltip="Copy ID"
            toast="Copied ID to clipboard"
            value={short_id}
            onClick={() => {
              Analytics.track(COPY_SHORT_LINK_SHORT_ID, {
                short_id: short_id
              });
            }}
          />
          <Token value={short_id} />
        </div>
      </div>
    </SectionBezel>
  );
};

export default LinkPreviewFields;
